import { create } from 'zustand'
import {
  Application,
  CreateApplicationData,
  UpdateApplicationData,
  listApplications,
  createApplication as apiCreate,
  updateApplication as apiUpdate,
  deleteApplication as apiDelete,
  getErrorMessage,
} from '../api/applicationsApi'

interface ApplicationsState {
  applications: Application[]
  isLoading: boolean
  error: string | null
  lastFetchIncludeInactive: boolean

  fetchApplications: (includeInactive?: boolean) => Promise<void>
  createApplication: (data: CreateApplicationData) => Promise<boolean>
  updateApplication: (appId: string, data: UpdateApplicationData) => Promise<boolean>
  deleteApplication: (appId: string) => Promise<boolean>
  clearError: () => void
}

export const useApplicationsStore = create<ApplicationsState>()((set, get) => ({
  applications: [],
  isLoading: false,
  error: null,
  lastFetchIncludeInactive: false,

  fetchApplications: async (includeInactive = false) => {
    set({ isLoading: true, error: null, lastFetchIncludeInactive: includeInactive })

    try {
      const response = await listApplications(includeInactive)
      set({ applications: response.applications, isLoading: false })
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
    }
  },

  createApplication: async (data: CreateApplicationData) => {
    set({ isLoading: true, error: null })

    try {
      await apiCreate(data)
      // Refetch to maintain server-side sort order
      const response = await listApplications(get().lastFetchIncludeInactive)
      set({ applications: response.applications, isLoading: false })
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  updateApplication: async (appId: string, data: UpdateApplicationData) => {
    set({ isLoading: true, error: null })

    try {
      await apiUpdate(appId, data)
      // Refetch to maintain server-side sort order (name may have changed)
      const response = await listApplications(get().lastFetchIncludeInactive)
      set({ applications: response.applications, isLoading: false })
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  deleteApplication: async (appId: string) => {
    set({ isLoading: true, error: null })

    try {
      await apiDelete(appId)
      set((state) => ({
        applications: state.applications.filter((app) => app.appId !== appId),
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  clearError: () => set({ error: null }),
}))
